import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { ControleResiduoForm, ControleResiduoSchema } from "../../../schemas/Obras_sustentabilidade/residuos_schema";
import { DefaultFormNumericInput } from "../../../../../../common/components/default_form_numeric_input";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { GetControleResiduoByIdResponseModel } from "../../../../domain/models/obras_sustentabilidade_model";
import { normalizeFileName } from "../../../../../../common/utils/utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function base64ToFile(base64: string, fileName: string): File {
  // Remove prefixo tipo: data:application/pdf;base64,...
  const arr = base64.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";

  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
}

type Props = {
  open: boolean;
  saving: boolean;
  editmod: boolean;
  onClose: () => void;
  onSubmit: (form: ControleResiduoForm) => void;
  onDownload: (arquivoNome: string, arquivo: File) => void;
  dados: GetControleResiduoByIdResponseModel | null;
};

export const ModalResiduo = ({ open, editmod, onClose, saving, onSubmit, onDownload, dados }: Props) => {
  const hookForm = useForm<ControleResiduoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ControleResiduoSchema),
  });

  const { reset, control, watch, setValue, handleSubmit } = hookForm;

  // Carrega os dados ao abrir o modal
  useEffect(() => {

    if (dados && open) {
      hookForm.setValue("idControleResiduos", dados.idControleResiduos ?? null);
      hookForm.setValue("idObra", dados.idObra ?? null);
      hookForm.setValue("ano", dados.ano ?? null);
      hookForm.setValue("idDescricaoMes", dados.idDescricaoMes ?? null);
      hookForm.setValue("volumeM3", dados.volumeM3 ?? null);
      hookForm.setValue("valorCacamba", dados.valorCacamba ?? null);
      hookForm.setValue("qtdeTotal", dados.qtdeTotal ?? null);
      hookForm.setValue("volumeTotal", dados.volumeTotal ?? null);
      hookForm.setValue("valorTotal", dados.valorTotal ?? null);
      hookForm.setValue("efetivoMes", dados.efetivoMes ?? null);
      hookForm.setValue("mediaTrabalhador", dados.mediaTrabalhador ?? null);
      hookForm.setValue("areaConstruida", dados.areaConstruida ?? null);
      hookForm.setValue("mediaArea", dados.mediaArea ?? null);
      hookForm.setValue("metaAprovacao", dados.metaAprovacao ?? null);
      hookForm.setValue("observacao", dados.observacao ?? null);
      hookForm.setValue("nomeArquivo1", dados.nomeArquivo1 ?? null);
      hookForm.setValue("limparArquivo1", false);

      const arquivo1Value = base64ToFile(dados.arquivo1 ?? '', dados.nomeArquivo1 ?? 'arquivo');
      hookForm.setValue("arquivo1", arquivo1Value);

    } else {
      reset({
        idControleResiduos: null,
        idObra: null,
        ano: null,
        idDescricaoMes: null,

        volumeM3: null,
        valorCacamba: null,
        qtdeTotal: null,
        volumeTotal: null,
        valorTotal: null,

        efetivoMes: null,
        mediaTrabalhador: null,
        areaConstruida: null,
        mediaArea: null,
        metaAprovacao: null,

        observacao: null,

        nomeArquivo1: null,
        arquivo1: null,
        limparArquivo1: false,
      });
    }
  }, [
    open,
    dados,
    hookForm,
    reset,
  ]);

  const nomeArquivo1 = watch("nomeArquivo1");
  const arquivo1 = watch("arquivo1");

  const handleDownload = useCallback(() => {
    if (!saving && arquivo1)
      onDownload(nomeArquivo1 ?? '', arquivo1);
  }, [
    saving,
    nomeArquivo1,
    arquivo1,
    onDownload,
  ]);


  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      const file = e.target.files.item(0);
      if (file) {
        hookForm.setValue('limparArquivo1', false);
        hookForm.setValue('nomeArquivo1', file.name);
        onChange(file);
      }
    }
  }, [
    saving,
    hookForm,
  ]);


  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('limparArquivo1', true);
        hookForm.setValue("nomeArquivo1", null);
        hookForm.setValue("arquivo1", null);

      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);



  return (
    <Dialog open={open} onClose={handleClose} sx={{ "& .MuiPaper-root": { width: "100%", maxWidth: "60rem" } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{editmod ? "Editar Resíduo" : "Cadastrar Resíduo"}</DialogTitle>
        <DialogContent dividers>
          <ModalLoading loading={saving}>
            <ModalContainer>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} mt={1}>
                {/* Coluna 1 */}
                <Box display="flex" flexDirection="column" gap={3}>
                  <FormMaskedInput name="ano" label="Ano" reactHookForm={hookForm} mask="0000" required={true} maskPlaceholder={"____"} />
                  <DefaultFormNumericInput name="volumeM3" label="Volume em M³" required={true} reactHookForm={hookForm} />
                  <DefaultFormNumericInput name="qtdeTotal" label="Qtde Total" reactHookForm={hookForm} />
                  <DefaultFormNumericInput name="areaConstruida" label="Área Construída" readOnly={true} reactHookForm={hookForm} />
                </Box>

                {/* Coluna 2 */}
                <Box display="flex" flexDirection="column" gap={3}>
                  <Controller
                    name="idDescricaoMes"
                    control={control}
                    render={({ field }) => (
                      <DefaultFormSelectInput
                        name="idDescricaoMes"
                        label="Mês"
                        reactHookForm={hookForm}
                        required={true}
                        options={[
                          { id: 1, label: "Janeiro" },
                          { id: 2, label: "Fevereiro" },
                          { id: 3, label: "Março" },
                          { id: 4, label: "Abril" },
                          { id: 5, label: "Maio" },
                          { id: 6, label: "Junho" },
                          { id: 7, label: "Julho" },
                          { id: 8, label: "Agosto" },
                          { id: 9, label: "Setembro" },
                          { id: 10, label: "Outubro" },
                          { id: 11, label: "Novembro" },
                          { id: 12, label: "Dezembro" },
                        ]}
                      />
                    )}
                  />
                  <FormMoneyMaskedInput name="valorCacamba" label="Valor Unitário" reactHookForm={hookForm} />
                  <FormMaskedInput name="efetivoMes" label="Efetivo do Mês" readOnly={true} reactHookForm={hookForm} mask="000000" maskPlaceholder={""} />
                  <DefaultFormNumericInput name="metaAprovacao" label="Meta de Aprovação" readOnly={true} reactHookForm={hookForm} />
                </Box>
              </Box>

              <Box mt={3}>
                <DefaultFormInput name="observacao" label="Observação" multiline rows={3} reactHookForm={hookForm} />
              </Box>

              <div className="flex w-full">
                <Controller
                  name='arquivo1'
                  control={hookForm.control}
                  render={({ field }) => (arquivo1 && arquivo1.size > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownload}>
                        {nomeArquivo1 && nomeArquivo1.length > 40
                          ? `${nomeArquivo1.substring(0, 40)}...`
                          : nomeArquivo1}
                      </span>
                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>

            </ModalContainer>
          </ModalLoading>
        </DialogContent>

        <DialogActions>
          <Box className="w-full flex justify-end gap-4 p-4">
            <Box className="flex gap-1">
              <Box className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1 className="text-sm text-black dark:text-white" onClick={handleClose}>
                  Cancelar
                </h1>
              </Box>
              <Box width="120px">
                {saving ? (
                  <PrimaryButton buttonColor="primary-green">{"Salvando..."}</PrimaryButton>
                ) : (
                  <PrimaryButton type="submit" buttonColor="primary-green">
                    {"Salvar"}
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
